import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, ReadOnlyBanner, Money } from "@/components/common";
import { FileDrop, MappingEditor, StatusPill, SummaryGrid } from "@/components/FileImport";
import EmployeePicker from "@/components/EmployeePicker";
import DatePicker, { isoToDmy } from "@/components/DatePicker";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api, errMsg, errCode, newKey } from "@/lib/api";
import { inr, todayISO, currentMonth, monthLabel, toPaise } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Individual({ disabled }) {
  const [emp, setEmp] = useState(null);
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ amount: "", date: todayISO(), reference: `Attendo ${monthLabel(currentMonth())}`, remarks: "" });
  const [key, setKey] = useState(newKey());
  const [warn, setWarn] = useState(null);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!emp) { setInfo(null); return; }
    api.get(`/employees/${emp.code}`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [emp]);

  const paise = toPaise(f.amount);
  const valid = emp && paise !== null && paise >= 0 && f.date;

  const submit = async (confirmExisting = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/transactions", { type: "opening", employee_code: emp.code, amount: f.amount, date: f.date, reference: f.reference, remarks: f.remarks, idempotency_key: key, confirm_existing_opening: confirmExisting });
      toast.success(`${data.transaction.type_label} saved for ${emp.name}. Outstanding: ${inr(data.balance_after)}`);
      setAsk(false); setWarn(null); setKey(newKey()); setF({ ...f, amount: "", remarks: "" });
      setInfo((await api.get(`/employees/${emp.code}`)).data);
    } catch (e) {
      if (errCode(e) === "OPENING_EXISTS") { setAsk(false); setWarn(e.response.data.detail); }
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div><Label>Employee</Label><div className="mt-1.5"><EmployeePicker value={emp} onChange={setEmp} testid="opening-employee-picker" /></div></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Opening outstanding balance (₹) *</Label><Input inputMode="decimal" className="mt-1.5 h-11 money text-lg" placeholder="0.00" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} disabled={disabled} data-testid="opening-amount-input" /></div>
          <div><Label>Effective date *</Label><DatePicker max={todayISO()} className="mt-1.5 h-11" value={f.date} onChange={(v) => setF({ ...f, date: v })} disabled={disabled} testid="opening-date-input" /></div>
          <div className="sm:col-span-2"><Label>Reference / Payroll month</Label><Input className="mt-1.5" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} disabled={disabled} data-testid="opening-reference-input" /></div>
          <div className="sm:col-span-2"><Label>Remarks</Label><Textarea className="mt-1.5" rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} disabled={disabled} data-testid="opening-remarks-input" /></div>
        </div>
        {f.amount && paise === null && <div className="text-sm text-red-700">Enter a valid amount.</div>}
        <Button className="w-full sm:w-auto bg-blue-800 hover:bg-blue-900" disabled={disabled || !valid || busy} onClick={() => setAsk(true)} data-testid="opening-save-btn">Save opening balance</Button>
      </div>
      <div className="lg:col-span-2 bg-slate-900 text-white rounded-xl p-5" data-testid="opening-current-card">
        <div className="text-[11px] uppercase tracking-wider text-slate-400">Current status</div>
        {!info ? <div className="text-slate-400 text-sm mt-3">Select an employee to see their current outstanding balance.</div> : (
          <div className="mt-3 space-y-3">
            <div><span className="money text-blue-300 text-sm">{info.code}</span> <span className="font-semibold">{info.name}</span></div>
            <div className="text-xs text-slate-400">Current outstanding</div>
            <div className="money text-3xl font-bold" data-testid="opening-current-balance">{inr(info.summary.current_balance)}</div>
            <div className="text-xs text-slate-400">{info.summary.transaction_count ? `Ledger exists · Opening ${inr(info.summary.opening_balance)} · ${info.summary.transaction_count} entries` : "No ledger yet — this will be recorded as the Opening Balance."}</div>
            {info.summary.transaction_count > 0 && <div className="text-xs text-amber-300 flex gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0" /> A ledger already exists. Saving will create a Balance Adjustment after confirmation.</div>}
          </div>
        )}
      </div>
      <ConfirmDialog open={ask} onOpenChange={setAsk} busy={busy} onConfirm={() => submit(false)} confirmText="Save" testid="opening-confirm-dialog"
        title="Confirm opening balance" description={emp && `${emp.code} – ${emp.name}: record outstanding balance of ${inr((paise || 0) / 100)} effective ${isoToDmy(f.date)}.`} />
      <ConfirmDialog open={!!warn} onOpenChange={(o) => !o && setWarn(null)} busy={busy} onConfirm={() => submit(true)} tone="warning" confirmText="Create adjustment" testid="opening-exists-dialog"
        title="Opening balance already exists" description={warn?.message}>
        {warn && <div className="grid grid-cols-3 gap-2 text-center text-sm">
          <div className="rounded-lg bg-slate-50 p-2"><div className="text-[11px] text-slate-500">Current</div><div className="money font-semibold">{inr(warn.current_balance)}</div></div>
          <div className="rounded-lg bg-slate-50 p-2"><div className="text-[11px] text-slate-500">Entered</div><div className="money font-semibold">{inr((paise || 0) / 100)}</div></div>
          <div className="rounded-lg bg-amber-50 p-2"><div className="text-[11px] text-amber-700">Adjustment</div><div className="money font-semibold text-amber-800">{inr(warn.adjustment)}</div></div>
        </div>}
      </ConfirmDialog>
    </div>
  );
}

const FIELDS = [["code", "Employee Code", true], ["name", "Employee Name"], ["amount", "Outstanding Balance", true], ["date", "Effective Date"], ["reference", "Reference / Payroll Month"], ["remarks", "Remarks"]];

function Bulk({ disabled }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [confirm, setConfirm] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const [summary, setSummary] = useState(null);

  const parse = async (f, mapping) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f); fd.append("kind", "balances");
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/import/parse", fd);
      setFile(f); setParsed(data); setConfirm(new Set()); setSummary(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/balances/commit", { rows: parsed.rows, confirm_codes: [...confirm], filename: parsed.filename });
      setSummary(data); setAsk(false); toast.success("Opening balances imported");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = (c, v) => { const s = new Set(confirm); v ? s.add(c) : s.delete(c); setConfirm(s); };
  const c = parsed?.counts || {};

  if (summary) return (
    <div className="space-y-4" data-testid="balance-import-summary">
      <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="h-5 w-5" /> Import completed</div>
      <SummaryGrid testid="balance-summary-grid" items={[["total", "Total rows", summary.total], ["opening", "Opening created", summary.opening_created, "text-blue-800"], ["adjustments", "Adjustments", summary.adjustments, "text-amber-700"], ["skipped", "Skipped", summary.skipped], ["duplicates", "Duplicates", summary.duplicates, "text-orange-700"], ["invalid", "Invalid", summary.invalid, "text-red-700"]]} />
      <Button variant="outline" onClick={() => { setParsed(null); setSummary(null); }}><RotateCcw className="h-4 w-4" /> Import another file</Button>
    </div>
  );
  if (!parsed) return <FileDrop onFile={(f) => parse(f)} busy={busy} testid="balance-import" template="balances" />;
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center text-sm text-slate-600"><span>File: <b>{parsed.filename}</b> · {parsed.total} rows</span><Button variant="outline" size="sm" onClick={() => setParsed(null)}><RotateCcw className="h-4 w-4" /> New file</Button></div>
      <MappingEditor key={JSON.stringify(parsed.mapping)} headers={parsed.headers} mapping={parsed.mapping} fields={FIELDS} busy={busy} testid="balance-import" onApply={(m) => parse(file, m)} />
      {parsed.missing_fields.length > 0 ? <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">Could not detect: {parsed.missing_fields.join(", ")}. Map the columns and click Apply.</div> : (
        <>
          <div className="bg-white rounded-xl border overflow-x-auto max-h-[480px]" data-testid="balance-preview-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0"><tr><th className="px-3 py-2 text-left">Row</th><th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Details</th></tr></thead>
              <tbody>
                {parsed.rows.map((r) => (
                  <tr key={r.row} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-400">{r.row}</td>
                    <td className="px-3 py-2"><span className="money text-blue-800">{r.code || "—"}</span> {r.name || r.file_name}</td>
                    <td className="px-3 py-2 text-right">{r.amount != null ? <Money value={r.amount} /> : "—"}</td>
                    <td className="px-3 py-2 money">{isoToDmy(r.date) || "—"}</td>
                    <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                    <td className="px-3 py-2 text-xs">
                      {r.error && <span className="text-red-700">{r.error}</span>}
                      {r.status === "existing" && <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={confirm.has(r.code)} onCheckedChange={(v) => toggle(r.code, v)} data-testid={`confirm-adjust-${r.code}`} /><span>Current {inr(r.current_balance)} → adjust by <b>{inr(r.adjustment)}</b></span></label>}
                      {r.status === "unchanged" && <span className="text-slate-500">Matches current balance — skipped</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl border p-4">
            <div className="text-sm text-slate-600">{c.new || 0} opening balances · {confirm.size} of {c.existing || 0} adjustments confirmed · {(c.invalid || 0) + (c.duplicate || 0)} skipped</div>
            <Button className="bg-blue-800 hover:bg-blue-900" disabled={disabled || busy || (c.new || 0) + confirm.size === 0} onClick={() => setAsk(true)} data-testid="balance-commit-btn">Import balances</Button>
          </div>
        </>
      )}
      <ConfirmDialog open={ask} onOpenChange={setAsk} busy={busy} onConfirm={commit} confirmText="Import now" testid="balance-confirm-dialog" title="Confirm opening balance import" description={`Create ${c.new || 0} opening balance entries and ${confirm.size} balance adjustments. Every entry is recorded in the audit trail.`} />
    </div>
  );
}

export default function OpeningBalances() {
  const { can } = useAuth();
  const ro = !can("admin", "payroll");
  return (
    <div>
      <PageHeader title="Opening Balances" subtitle="Enter existing outstanding advances from Petpooja Attendo. Recorded as Opening Balance — not as a new advance." />
      {ro && <ReadOnlyBanner />}
      <Tabs defaultValue="single">
        <TabsList className="mb-4"><TabsTrigger value="single" data-testid="opening-tab-single">Individual entry</TabsTrigger><TabsTrigger value="bulk" data-testid="opening-tab-bulk" disabled={ro}>Bulk import (Excel/CSV)</TabsTrigger></TabsList>
        <TabsContent value="single"><Individual disabled={ro} /></TabsContent>
        <TabsContent value="bulk"><Bulk disabled={ro} /></TabsContent>
      </Tabs>
    </div>
  );
}
